/**
 * @fileoverview Type definitions for the Outreach module.
 * @module store/modules/outreach/types
 */

import { ATTRIBUTE_TYPE } from "@/helperMethods/outreach/attribute";

/**
 * Outreach module state structure.
 *
 * @property {Object} contacts - Contacts data with array and count
 * @property {Object} tags - Tags for contact segmentation
 * @property {Object} stages - Stage definitions for contact workflow
 * @property {Record<string, OutReachMetric>} metrics - Filter group metrics
 * @property {boolean} isLoading - Loading state
 * @property {Array} filterBoard - Active filter configuration
 * @property {Object} sortBoard - Sort configuration
 * @property {ContactAttribute[]} contactAttributes - Custom contact attributes
 */
export type OutreachState = {
  contacts: {
    data: Partial<OutReachContact>[];
    count: number;
  };
  tags: {
    data: Partial<OutReachTag>[];
  };
  stages: {
    data: Partial<OutReachTag>[];
  };
  metrics: Record<string, OutReachMetric>;
  isLoading: boolean;
  filterBoard: [];
  sortBoard: {};
  contactAttributes: ContactAttribute[];
};

/**
 * Outreach contact entity representing a customer or lead.
 *
 * @property {string} id - Contact unique identifier
 * @property {string} name - Contact name
 * @property {string} primaryContact - Primary contact method (email/phone)
 * @property {string} channel - Communication channel
 * @property {OutReachTag[]} tags - Associated tags
 * @property {OutReachTag} stage - Current workflow stage
 * @property {string} country - Country location
 * @property {string} lastSeen - Last interaction timestamp
 * @property {string} ID - Alternative ID field
 * @property {boolean} isDuplicate - Duplicate detection flag
 * @property {ContactAttribute[]} contactattributes - Custom attributes
 */
export class OutReachContact {
  id!: string;
  name!: string;
  primaryContact!: string;
  channel!: string;
  tags!: OutReachTag[];
  stage!: OutReachTag;
  country!: string;
  lastSeen!: string;
  ID!: string;
  isDuplicate!: boolean;
  contactattributes!: ContactAttribute[];
}

/**
 * Tag or stage for contact segmentation.
 *
 * @property {string} id - Tag unique identifier
 * @property {string} name - Tag display name
 * @property {OUT_REACH_TAG_TYPE} type - Tag type (TAG or STAGE)
 */
export interface OutReachTag {
  id: string;
  name: string;
  type: OUT_REACH_TAG_TYPE;
}

/**
 * Outreach metric for filtered contact counts.
 *
 * @property {number} id - Metric unique identifier
 * @property {string} name - Metric display name
 * @property {number} value - Contact count matching filters
 * @property {string} color - Display color code
 * @property {Record} filterList - Filter criteria definitions
 */
export interface OutReachMetric {
  id: number;
  name: string;
  value: number;
  color: string;
  filterList: Record<
    number,
    {
      variable: string;
      comparison: string;
      value: string;
      valueArray: string[];
    }
  >;
}

/**
 * Custom contact attribute definition.
 *
 * @property {number} id - Attribute unique identifier
 * @property {string} name - Attribute internal name
 * @property {string} label - Attribute display label
 * @property {ATTRIBUTE_TYPE} type - Attribute data type
 * @property {string[]} value - Possible values (for dropdowns)
 * @property {boolean} isDeleted - Soft deletion flag
 */
export interface ContactAttribute {
  id: number;
  name: string;
  label: string;
  type: ATTRIBUTE_TYPE;
  value: string[];
  isDeleted?: boolean;
}

/**
 * Enum for outreach tag types.
 */
export enum OUT_REACH_TAG_TYPE {
  TAG = "TAG",
  STAGE = "STAGE",
}

/**
 * Outreach automation configuration for scheduled messaging campaigns.
 *
 * @property {string} id - Automation unique identifier
 * @property {string} bot - Associated bot ID
 * @property {string} name - Automation name
 * @property {string} context - Automation context/description
 * @property {boolean} enabled - Active status
 * @property {AutomationChannel[]} channels - Communication channels
 * @property {AutomationMessage[]} messages - Messages to send
 * @property {number[]} contactTags - Target contact tags
 * @property {number[]} contactStages - Target contact stages
 * @property {number[]} contactList - Target contact IDs
 * @property {AutomationContactAttributes[]} contactAttributes - Attribute filters
 * @property {string[]} contactOperations - Logical operations (AND/OR)
 * @property {AutomationStateVariables[]} stateVariables - State variable filters
 * @property {AUTOMATION_SCHEDULE_TYPE} scheduleType - Schedule type
 * @property {string[]} scheduleCron - Cron expressions for recurring
 * @property {Date} startDate - Start date for scheduled
 * @property {Date} endDate - End date for scheduled
 * @property {Date} sentTime - Last sent timestamp
 * @property {AutomationUpdate[]} updates - Contact updates to perform
 * @property {boolean} isDeleted - Soft deletion flag
 * @property {Date} deletedAt - Deletion timestamp
 * @property {Date} createdAt - Creation timestamp
 * @property {Date} updatedAt - Last update timestamp
 * @property {AUTOMATION_RECURRING_MODE} recurringMode - Recurring mode type
 * @property {AUTOMATION_STATE_CHANGE_TYPE} stateType - State change trigger type
 * @property {string} stateTypeId - State change trigger ID
 * @property {AUTOMATION_RECURRING_SCHEDULE} sendSchedule - Send timing
 * @property {Object} sendScheduled - Scheduled delay configuration
 */
export interface OutreachAutomation {
  id: string;
  bot: string;
  name: string;
  context?: string;
  enabled: boolean;
  channels: AutomationChannel[];
  messages: AutomationMessage[];
  contactTags: number[];
  contactStages: number[];
  contactList: number[];
  contactAttributes: AutomationContactAttributes[];
  contactOperations: string[];
  stateVariables: AutomationStateVariables[];
  scheduleType: AUTOMATION_SCHEDULE_TYPE;
  scheduleCron: string[];
  startDate?: Date;
  endDate?: Date;
  sentTime?: Date;
  updates: AutomationUpdate[];
  isDeleted?: boolean;
  deletedAt?: Date;
  createdAt?: Date;
  updatedAt?: Date;
  recurringMode?: AUTOMATION_RECURRING_MODE;
  stateType?: AUTOMATION_STATE_CHANGE_TYPE;
  stateTypeId?: string;
  sendSchedule?: AUTOMATION_RECURRING_SCHEDULE;
  sendScheduled?: {
    value: number;
    unit: "DAY" | "WEEK" | "MONTH";
  };
}

export type AutomationMessage = {
  contentType: AUTOMATION_MESSAGE_CONTENT_TYPE;
  contentId: string;
  contentName?: string;
  customMessage?: string;
  contentParameters?: Record<string, any>;
};

export type AutomationUpdate = {
  property: string;
  updateTo: number[];
  updateItem?: Record<string, any>;
  method: AUTOMATION_UPDATE_METHOD;
  failed?: boolean;
};

export type AutomationChannel = {
  name: AUTOMATION_CHANNEL_NAME;
  settings: Record<string, any>;
};

export type AutomationContactAttributes = {
  attribute: string;
  operator: AUTOMATION_ATTRIBUTE_OPERATOR;
  value: string;
};

export type AutomationStateVariables = {
  variable: string;
  operator: AUTOMATION_ATTRIBUTE_OPERATOR;
  value: string;
};

export enum AUTOMATION_SCHEDULE_TYPE {
  NOT_SET = "NOT_SET",
  SEND_NOW = "SEND_NOW",
  SCHEDULED = "SCHEDULED",
  RECURRING = "RECURRING",
}

export enum AUTOMATION_RECURRING_MODE {
  FIXED_DATE = "FIXED_DATE",
  STATE_CHANGE = "STATE_CHANGE",
}

export enum AUTOMATION_RECURRING_SCHEDULE {
  IMMEDIATE = "IMMEDIATE",
  SCHEDULED = "SCHEDULED",
}

export enum AUTOMATION_STATE_CHANGE_TYPE {
  TAG = "TAG",
  STAGE = "STAGE",
}

export enum AUTOMATION_UPDATE_METHOD {
  OVERRIDE = "OVERRIDE",
  ADD = "ADD",
  REMOVE = "REMOVE",
}

export enum AUTOMATION_MESSAGE_CONTENT_TYPE {
  MESSAGE = "MESSAGE",
  CONTENT_NODE = "CONTENT_NODE",
  WA_MESSAGE_TEMPLATE = "WA_MESSAGE_TEMPLATE",
}

export enum AUTOMATION_MESSAGE_PARAMETER_TYPE {
  TEXT = "TEXT",
  VARIABLE = "VARIABLE",
}

export enum AUTOMATION_CHANNEL_NAME {
  WHATSAPP = "WHATSAPP",
  EMAIL = "EMAIL",
  API_INTEGRATION = "API_INTEGRATION",
}

export enum AUTOMATION_OPERATION {
  AND = "AND",
  OR = "OR",
  TAGS = "TAGS",
  LIST = "LIST",
  STAGES = "STAGES",
  ATTRIBUTES = "ATTRIBUTES",
}

export enum AUTOMATION_ATTRIBUTE_OPERATOR {
  EQUAL = "EQUAL",
  NOT_EQUAL = "NOT_EQUAL",
  LESS_THAN = "LESS_THAN",
  GREATER_THAN = "GREATER_THAN",
}
